/**
 * Вкладка «Состав» окна пряника или набора.
 *
 * Набор PP1 — плитки фигур: превью, артикул (моноширинным, с копированием),
 * название и количество; клик открывает окно фигуры поверх набора, «Назад»
 * возвращает к набору. Набор PP2 — сетка кругов (у кругов артикула нет, поэтому
 * без перехода). Одиночный пряник — «Входит в наборы» такими же плитками.
 *
 * Эти данные раньше рисовала вкладка «Производство» (список со ссылками на
 * страницу каталога); здесь они живут один раз и переиспользуются.
 */

import { Link } from 'react-router-dom'
import { Boxes, Cookie, ImageOff, Loader2 } from 'lucide-react'
import { CopyButton } from '../../../components/ui/CopyButton'
import { useEntityNavigator } from '../../../components/entity-view/navigatorContext'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { catalogHref, isPp2Kit } from '../lib'
import type { GingerbreadBlock, GingerbreadComponent, GingerbreadUsedIn } from '../types'
import { noProductPreviewUrl } from './NoProductTab'

const sectionTitle = 'mb-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

/** Круг набора PP2: у него нет артикула, поэтому без ссылки — превью и название. */
export function CircleTile({ component, index }: { component: GingerbreadComponent; index: number }) {
  const src = component.previewUrl || (component.gingerbreadId ? noProductPreviewUrl(component.gingerbreadId) : null)
  const name = component.name || component.title || `Круг ${component.position ?? index + 1}`
  return (
    <li className="min-w-0 text-center" data-testid="pp2-circle">
      <div className="mx-auto flex aspect-square w-full items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
        {src ? (
          <AuthorizedMediaImage
            src={src}
            alt={name}
            className="h-full w-full object-cover"
            errorFallback={<ImageOff aria-label="Превью недоступно" className="h-5 w-5 text-gray-300 dark:text-zinc-600" />}
          />
        ) : (
          <ImageOff aria-label="Нет изображения" className="h-5 w-5 text-gray-300 dark:text-zinc-600" />
        )}
      </div>
      <p className="mt-1 truncate text-2xs text-gray-600 dark:text-zinc-400" title={name}>
        <span className="mr-1 tabular-nums text-gray-400 dark:text-zinc-500">{component.position ?? index + 1}.</span>
        {name}
      </p>
    </li>
  )
}

interface TileProps {
  article: string
  title: string | null
  quantity?: number | null
  position?: number | null
  previewSrc?: string | null
  fallbackIcon: 'figure' | 'kit'
}

/** Плитка фигуры или набора: клик открывает окно товара поверх текущего. */
function ArticleTile({ article, title, quantity, position, previewSrc, fallbackIcon }: TileProps) {
  const navigator = useEntityNavigator()
  const Icon = fallbackIcon === 'kit' ? Boxes : Cookie
  const body = (
    <>
      <span className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
        {previewSrc ? (
          <AuthorizedMediaImage
            src={previewSrc}
            alt={`Превью ${article}`}
            className="h-full w-full object-contain"
            errorFallback={<Icon aria-label="Превью недоступно" className="h-8 w-8 text-gray-300 dark:text-zinc-600" />}
          />
        ) : (
          <Icon aria-hidden="true" className="h-8 w-8 text-gray-300 dark:text-zinc-600" />
        )}
      </span>
      {title && (
        <span className="mt-1 block truncate text-xs text-gray-600 dark:text-zinc-300" title={title}>
          {position != null && <span className="mr-1 tabular-nums text-gray-400 dark:text-zinc-500">{position}.</span>}
          {title}
        </span>
      )}
    </>
  )
  const open = () => navigator.open({ kind: 'product', article })
  return (
    <li className="min-w-0" data-testid="composition-tile">
      {navigator.available ? (
        <button
          type="button"
          onClick={open}
          aria-label={`Открыть ${article}`}
          className="block w-full rounded-md p-1 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:bg-zinc-800/60"
        >
          {body}
        </button>
      ) : (
        <Link to={catalogHref(article)} className="block rounded-md p-1 hover:bg-gray-50 dark:hover:bg-zinc-800/60">
          {body}
        </Link>
      )}
      <div className="mt-0.5 flex items-center justify-between gap-1 px-1">
        <span className="inline-flex min-w-0 items-center gap-0.5">
          <span className="truncate font-mono text-[0.8125rem] text-gray-900 dark:text-zinc-100">{article}</span>
          <CopyButton value={article} label="артикул" size="xs" />
        </span>
        {quantity != null && (
          <span className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-zinc-400">× {quantity}</span>
        )}
      </div>
    </li>
  )
}

const grid = 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'

/** Состав набора: PP2 — круги, PP1 — плитки фигур. */
export function KitComposition({ block }: { block: GingerbreadBlock }) {
  const components = block.components
  if (components.length === 0) {
    return <p className="text-xs text-gray-500 dark:text-zinc-400">Состав не записан.</p>
  }
  if (isPp2Kit(block)) {
    return (
      <ul className="grid grid-cols-3 gap-3 sm:grid-cols-6" aria-label="Состав набора">
        {components.map((component, index) => (
          <CircleTile
            key={`${component.gingerbreadId ?? 'circle'}:${component.position ?? index}`}
            component={component}
            index={index}
          />
        ))}
      </ul>
    )
  }
  return (
    <ul className={grid} aria-label="Состав набора">
      {components.map((component, index) =>
        component.article ? (
          <ArticleTile
            key={`${component.article}:${component.position ?? index}`}
            article={component.article}
            title={component.title}
            quantity={component.quantity}
            position={component.position}
            previewSrc={component.previewUrl || (component.gingerbreadId ? noProductPreviewUrl(component.gingerbreadId) : null)}
            fallbackIcon="figure"
          />
        ) : (
          <li key={`${component.gingerbreadId ?? ''}:${component.position ?? index}`} className="text-sm text-gray-900 dark:text-zinc-100">
            {component.name || 'Без артикула'}
          </li>
        ),
      )}
    </ul>
  )
}

/** «Входит в наборы» у одиночного пряника. */
export function UsedInTiles({ usedIn }: { usedIn: GingerbreadUsedIn[] }) {
  if (usedIn.length === 0) {
    return <p className="text-xs text-gray-500 dark:text-zinc-400">В наборы пока не входит.</p>
  }
  return (
    <ul className={grid} aria-label="Входит в наборы">
      {usedIn.map((kit) => (
        <ArticleTile key={kit.article} article={kit.article} title={kit.title} fallbackIcon="kit" />
      ))}
    </ul>
  )
}

export function CompositionTab({
  block,
  loading,
}: {
  block: GingerbreadBlock | null | undefined
  loading?: boolean
}) {
  if (loading || block === undefined) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-label="Загрузка" />
      </div>
    )
  }
  if (!block) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        Для этого товара нет производственных данных генератора пряников.
      </p>
    )
  }
  const isKit = block.kind === 'kit'
  return (
    <section data-testid="gingerbread-composition">
      <h4 className={sectionTitle}>{isKit ? 'Состав набора' : 'Входит в наборы'}</h4>
      {isKit ? <KitComposition block={block} /> : <UsedInTiles usedIn={block.usedIn} />}
    </section>
  )
}
